import type JSZip from "jszip";
import type { POI, Frame, PanoramaWithUrl } from "../api/types";

export interface CollectImagesInput {
  pois: POI[];
  frames: Frame[];
  /** Frames whose `poiId` is set — already excluded from `frames` because the
   *  POI placemark represents them. We pull a full-res JPEG from each POI's
   *  most-recent linked frame so the POI balloon can offer a real "open full
   *  size" click-through instead of just the 320x180 in-line thumb. */
  poiLinkedFrames?: Frame[];
  panoramas: PanoramaWithUrl[];
  /** Resolve a presigned GET URL for a frame variant. */
  frameUrl: (frameId: string, variant: "full" | "thumb") => Promise<string>;
}

export interface CollectImagesResult {
  hrefs: Map<string, string>;
  failures: number;
}

/** Decode a `data:` URL into a Blob. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(",", 2);
  const head = parts[0] ?? "";
  const b64 = parts[1] ?? "";
  const mime = /data:([^;]+)/.exec(head)?.[1] ?? "application/octet-stream";
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: mime });
}

async function fetchBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.blob();
}

/**
 * Downscale an equirectangular panorama Blob to a ~640px-wide JPEG thumbnail.
 * Returns null if canvas decoding is unavailable (e.g. headless test env).
 */
async function makePanoThumb(full: Blob): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(full);
    const w = 640;
    const h = Math.round((bitmap.height / bitmap.width) * w);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    return await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.82));
  } catch {
    return null;
  }
}

/**
 * Collect every annotation image into `zip` under `images/`. Each failure
 * degrades only that image: its key is omitted and `failures` is incremented.
 */
export async function collectImages(
  zip: JSZip, input: CollectImagesInput,
): Promise<CollectImagesResult> {
  const hrefs = new Map<string, string>();
  let failures = 0;
  const folder = "images";

  for (const poi of input.pois) {
    if (poi.thumb === undefined) continue;
    try {
      const path = `${folder}/poi-${poi.id}-thumb.jpg`;
      zip.file(path, dataUrlToBlob(poi.thumb));
      hrefs.set(`poi-thumb:${poi.id}`, path);
    } catch { failures += 1; }
  }

  // POI full-res from the POI's most-recent linked frame, when available.
  // Lets the KMZ balloon offer a real click-through; the POI's own 320x180
  // inline thumb is the only image POIs carry directly.
  const linkedByPoi = new Map<string, Frame>();
  for (const f of input.poiLinkedFrames ?? []) {
    if (!f.poiId || !f.hasFull) continue;
    const cur = linkedByPoi.get(f.poiId);
    if (cur === undefined || f.capturedAt > cur.capturedAt) {
      linkedByPoi.set(f.poiId, f);
    }
  }
  for (const poi of input.pois) {
    const linked = linkedByPoi.get(poi.id);
    if (linked === undefined) continue;
    try {
      const url = await input.frameUrl(linked.id, "full");
      const blob = await fetchBlob(url);
      const path = `${folder}/poi-${poi.id}-full.jpg`;
      zip.file(path, blob);
      hrefs.set(`poi-full:${poi.id}`, path);
    } catch { failures += 1; }
  }

  for (const frame of input.frames) {
    for (const variant of ["full", "thumb"] as const) {
      const present = variant === "full" ? frame.hasFull : frame.hasThumb;
      if (!present) continue;
      try {
        const url = await input.frameUrl(frame.id, variant);
        const blob = await fetchBlob(url);
        const suffix = variant === "full" ? "" : "-thumb";
        const path = `${folder}/frame-${frame.id}${suffix}.jpg`;
        zip.file(path, blob);
        hrefs.set(`frame-${variant}:${frame.id}`, path);
      } catch { failures += 1; }
    }
  }

  for (const pano of input.panoramas) {
    try {
      const full = await fetchBlob(pano.url);
      const fullPath = `${folder}/pano-${pano.id}.jpg`;
      zip.file(fullPath, full);
      hrefs.set(`pano-full:${pano.id}`, fullPath);
      const thumb = await makePanoThumb(full);
      if (thumb !== null) {
        const thumbPath = `${folder}/pano-${pano.id}-thumb.jpg`;
        zip.file(thumbPath, thumb);
        hrefs.set(`pano-thumb:${pano.id}`, thumbPath);
      }
    } catch { failures += 1; }
  }

  return { hrefs, failures };
}
